import React, { useState, useEffect } from 'react';
import Alert from '@mui/material/Alert';
import { withoutAuth } from '../../hooks/useAuth';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiMail, FiLock, FiUser, FiPhone, FiMapPin, FiChevronLeft, FiChevronRight } from 'react-icons/fi';

import Logo from "../../assets/img/favicon.png";

function Register() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstname: '',
    lastname: '',
    country: 'italia',
    address: '',
    city: '',
    postalCode: '',
    phone: '',
  });
  const [errors, setErrors] = useState({});
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('error');
  const [isLoading, setIsLoading] = useState(false);

  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.5,
        ease: "easeOut"
      }
    })
  };

  const buttonVariants = {
    idle: { scale: 1 },
    hover: { scale: 1.05 },
    tap: { scale: 0.95 }
  };

  const pageTransition = {
    initial: { opacity: 0, x: 100 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -100 },
    transition: { duration: 0.5 }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
    
    // Clear error when field is updated
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const validateStep = () => {
    const newErrors = {};
    if (step === 1) {
      if (!formData.email) newErrors.email = 'Email richiesta';
      else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email non valida';
      
      if (!formData.password) newErrors.password = 'Password richiesta';
      else if (formData.password.length < 6) newErrors.password = 'Password troppo corta (min 6 caratteri)';
      
      if (formData.password !== formData.confirmPassword)
        newErrors.confirmPassword = 'Le password non coincidono';
    }
    if (step === 2) {
      if (!formData.firstname) newErrors.firstname = 'Nome richiesto';
      if (!formData.lastname) newErrors.lastname = 'Cognome richiesto';
      if (!formData.address) newErrors.address = 'Indirizzo richiesto';
      if (!formData.city) newErrors.city = 'Città richiesta';
      if (!formData.phone) newErrors.phone = 'Telefono richiesto';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      setStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = async () => {
    if (validateStep()) {
      setIsLoading(true);
      try {
        const response = await fetch(process.env.REACT_APP_BACKEND_URI + 'user/create', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });
        const data = await response.json();
        
        if (response.ok) {
          setAlertMessage('Registrazione completata con successo!');
          setAlertType('success');
          setAlertOpen(true);
          setTimeout(() => (window.location.href = '/login'), 3000);
        } else {
          setAlertMessage(data.message || 'Errore durante la registrazione');
          setAlertType('error');
          setAlertOpen(true);
        }
      } catch (error) {
        setAlertMessage('Si è verificato un errore. Riprova più tardi.');
        setAlertType('error');
        setAlertOpen(true);
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (alertOpen) {
      const timer = setTimeout(() => {
        setAlertOpen(false);
      }, 10000);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [alertOpen]);

  return (
    <section className="min-h-[100vh] bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center py-12">
      <motion.div 
        className="w-full max-w-lg p-8 md:p-10 bg-white rounded-2xl shadow-xl relative overflow-hidden"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        {/* Progress bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gray-100">
          <motion.div 
            className="h-full bg-gradient-to-r from-[#08a5ec] to-[#126eeb]"
            initial={{ width: "50%" }}
            animate={{ width: step === 1 ? "50%" : "100%" }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
          />
        </div>
        
        <div className="flex flex-col items-center">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Link to={"/"}>
              <img
                alt="Marrocco ART"
                src={Logo}
                className="h-20 w-auto"
              />
            </Link>
          </motion.div>
          <motion.h2 
            className="mt-6 text-center text-2xl font-bold text-gray-900"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            {step === 1 ? 'Crea il tuo account' : 'Completa i tuoi dati'}
          </motion.h2>
          <motion.p
            className="mt-2 text-center text-sm text-gray-600"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
          >
            {step === 1 
              ? 'Inserisci le credenziali per il tuo nuovo account' 
              : 'Aggiungi le informazioni personali per completare la registrazione'}
          </motion.p>
        </div>

        <div className="mt-8">
          {/* Step 1 Form */}
          {step === 1 && (
            <motion.form
              className="space-y-5"
              initial="hidden"
              animate="visible"
              variants={fadeIn}
              key="step1"
              {...pageTransition}
            >
              <motion.div variants={fadeIn} custom={1}>
                <div className="flex items-center space-x-2 mb-2">
                  <FiMail className="text-gray-500" />
                  <label htmlFor="email" className="text-sm font-medium text-gray-700">
                    Email
                  </label>
                </div>
                <div className="relative">
                  <input 
                    type="email"
                    name="email"
                    id="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 text-gray-800 bg-gray-50 border ${errors.email ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:ring-2 focus:ring-[#08a5ec] focus:border-transparent transition-colors duration-200`}
                    placeholder="Il tuo indirizzo email"
                  />
                  {errors.email && (
                    <p className="mt-1 text-xs text-red-500">{errors.email}</p>
                  )}
                </div>
              </motion.div>

              <motion.div variants={fadeIn} custom={2}>
                <div className="flex items-center space-x-2 mb-2">
                  <FiLock className="text-gray-500" />
                  <label htmlFor="password" className="text-sm font-medium text-gray-700">
                    Password
                  </label>
                </div>
                <div className="relative">
                  <input 
                    type="password"
                    name="password"
                    id="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 text-gray-800 bg-gray-50 border ${errors.password ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:ring-2 focus:ring-[#08a5ec] focus:border-transparent transition-colors duration-200`}
                    placeholder="Crea una password sicura"
                  />
                  {errors.password && (
                    <p className="mt-1 text-xs text-red-500">{errors.password}</p>
                  )}
                </div>
              </motion.div>

              <motion.div variants={fadeIn} custom={3}>
                <div className="flex items-center space-x-2 mb-2">
                  <FiLock className="text-gray-500" />
                  <label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">
                    Conferma Password
                  </label>
                </div>
                <div className="relative">
                  <input 
                    type="password"
                    name="confirmPassword"
                    id="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 text-gray-800 bg-gray-50 border ${errors.confirmPassword ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:ring-2 focus:ring-[#08a5ec] focus:border-transparent transition-colors duration-200`}
                    placeholder="Ripeti la password"
                  />
                  {errors.confirmPassword && (
                    <p className="mt-1 text-xs text-red-500">{errors.confirmPassword}</p>
                  )}
                </div>
              </motion.div>
            </motion.form>
          )}

          {/* Step 2 Form */}
          {step === 2 && (
            <motion.form
              className="space-y-5"
              initial="hidden"
              animate="visible"
              variants={fadeIn}
              key="step2"
              {...pageTransition}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <motion.div variants={fadeIn} custom={1}>
                  <div className="flex items-center space-x-2 mb-2">
                    <FiUser className="text-gray-500" />
                    <label htmlFor="firstname" className="text-sm font-medium text-gray-700">
                      Nome
                    </label>
                  </div>
                  <div className="relative">
                    <input 
                      type="text"
                      name="firstname"
                      id="firstname"
                      value={formData.firstname}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 text-gray-800 bg-gray-50 border ${errors.firstname ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:ring-2 focus:ring-[#08a5ec] focus:border-transparent transition-colors duration-200`}
                      placeholder="Il tuo nome"
                    />
                    {errors.firstname && (
                      <p className="mt-1 text-xs text-red-500">{errors.firstname}</p>
                    )}
                  </div>
                </motion.div>

                <motion.div variants={fadeIn} custom={1}>
                  <div className="flex items-center space-x-2 mb-2">
                    <FiUser className="text-gray-500" />
                    <label htmlFor="lastname" className="text-sm font-medium text-gray-700">
                      Cognome
                    </label>
                  </div>
                  <div className="relative">
                    <input 
                      type="text"
                      name="lastname"
                      id="lastname"
                      value={formData.lastname}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 text-gray-800 bg-gray-50 border ${errors.lastname ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:ring-2 focus:ring-[#08a5ec] focus:border-transparent transition-colors duration-200`}
                      placeholder="Il tuo cognome"
                    />
                    {errors.lastname && (
                      <p className="mt-1 text-xs text-red-500">{errors.lastname}</p>
                    )}
                  </div>
                </motion.div>
              </div>

              <motion.div variants={fadeIn} custom={2}>
                <div className="flex items-center space-x-2 mb-2">
                  <FiMapPin className="text-gray-500" />
                  <label htmlFor="address" className="text-sm font-medium text-gray-700">
                    Indirizzo
                  </label>
                </div>
                <div className="relative">
                  <input 
                    type="text"
                    name="address"
                    id="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 text-gray-800 bg-gray-50 border ${errors.address ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:ring-2 focus:ring-[#08a5ec] focus:border-transparent transition-colors duration-200`}
                    placeholder="Via e numero civico"
                  />
                  {errors.address && (
                    <p className="mt-1 text-xs text-red-500">{errors.address}</p>
                  )}
                </div>
              </motion.div>

              <div className="grid grid-cols-2 gap-5">
                <motion.div variants={fadeIn} custom={3}>
                  <div className="flex items-center space-x-2 mb-2">
                    <FiMapPin className="text-gray-500" />
                    <label htmlFor="city" className="text-sm font-medium text-gray-700">
                      Città
                    </label>
                  </div>
                  <div className="relative">
                    <input 
                      type="text"
                      name="city"
                      id="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 text-gray-800 bg-gray-50 border ${errors.city ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:ring-2 focus:ring-[#08a5ec] focus:border-transparent transition-colors duration-200`}
                      placeholder="La tua città"
                    />
                    {errors.city && (
                      <p className="mt-1 text-xs text-red-500">{errors.city}</p>
                    )}
                  </div>
                </motion.div>

                <motion.div variants={fadeIn} custom={3}>
                  <div className="flex items-center space-x-2 mb-2">
                    <FiMapPin className="text-gray-500" />
                    <label htmlFor="postalCode" className="text-sm font-medium text-gray-700">
                      CAP
                    </label>
                  </div>
                  <div className="relative">
                    <input 
                      type="text"
                      name="postalCode"
                      id="postalCode"
                      value={formData.postalCode}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 text-gray-800 bg-gray-50 border ${errors.postalCode ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:ring-2 focus:ring-[#08a5ec] focus:border-transparent transition-colors duration-200`}
                      placeholder="00000"
                    />
                    {errors.postalCode && (
                      <p className="mt-1 text-xs text-red-500">{errors.postalCode}</p>
                    )}
                  </div>
                </motion.div>
              </div>

              <motion.div variants={fadeIn} custom={4}>
                <div className="flex items-center space-x-2 mb-2">
                  <FiPhone className="text-gray-500" />
                  <label htmlFor="phone" className="text-sm font-medium text-gray-700">
                    Telefono
                  </label>
                </div>
                <div className="relative">
                  <input 
                    type="tel"
                    name="phone"
                    id="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 text-gray-800 bg-gray-50 border ${errors.phone ? 'border-red-500' : 'border-gray-200'} rounded-lg focus:ring-2 focus:ring-[#08a5ec] focus:border-transparent transition-colors duration-200`}
                    placeholder="Il tuo numero di telefono"
                  />
                  {errors.phone && (
                    <p className="mt-1 text-xs text-red-500">{errors.phone}</p>
                  )}
                </div>
              </motion.div>
            </motion.form>
          )}

          {/* Navigation Buttons */}
          <div className="mt-8 flex justify-between">
            {step > 1 && (
              <motion.button
                variants={buttonVariants}
                initial="idle"
                whileHover="hover"
                whileTap="tap"
                onClick={handleBack}
                className="flex items-center px-5 py-3 text-gray-700 bg-gray-100 rounded-lg font-medium transition-all duration-200 hover:bg-gray-200"
              >
                <FiChevronLeft className="mr-2" />
                Indietro
              </motion.button>
            )}
            
            {step === 1 ? (
              <motion.button
                variants={buttonVariants}
                initial="idle"
                whileHover="hover"
                whileTap="tap"
                onClick={handleNext}
                className="ml-auto flex items-center px-5 py-3 text-white bg-gradient-to-r from-[#08a5ec] to-[#126eeb] rounded-lg font-medium shadow-md"
              >
                Continua
                <FiChevronRight className="ml-2" />
              </motion.button>
            ) : (
              <motion.button
                variants={buttonVariants}
                initial="idle"
                whileHover="hover"
                whileTap="tap"
                onClick={handleSubmit}
                disabled={isLoading}
                className="ml-auto flex items-center px-5 py-3 text-white bg-gradient-to-r from-[#08a5ec] to-[#126eeb] rounded-lg font-medium shadow-md disabled:opacity-70"
              >
                {isLoading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Registrazione...
                  </span>
                ) : (
                  <span>Completa Registrazione</span>
                )}
              </motion.button>
            )}
          </div>

          <motion.div
            className="mt-8 text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
          >
            <p className="text-sm text-gray-600">
              Hai già un account?{' '}
              <Link to="/login" className="font-medium text-[#08a5ec] hover:text-[#126eeb] transition-colors duration-200">
                Accedi qui
              </Link>
            </p>
          </motion.div>
        </div>
      </motion.div>

      {/* Alert component */}
      <motion.div 
        className="fixed top-8 right-8 z-50"
        initial={{ opacity: 0, y: -20 }}
        animate={{ 
          opacity: alertOpen ? 1 : 0,
          y: alertOpen ? 0 : -20,
          pointerEvents: alertOpen ? 'auto' : 'none'
        }}
        transition={{ duration: 0.3 }}
      >
        {alertOpen && (
          <Alert 
            severity={alertType} 
            onClose={() => setAlertOpen(false)}
            className="shadow-lg border border-white/10"
            sx={{
              backgroundColor: alertType === 'success' ? '#10B981' : '#EF4444',
              color: 'white',
              '& .MuiAlert-icon': {
                color: 'white'
              }
            }}
          >
            {alertMessage}
          </Alert>
        )}
      </motion.div>
    </section>
  );
}

export default withoutAuth(Register);